import { LitElement, html } from 'lit';
import { customElement, property, query, queryAll, state } from 'lit/decorators.js';
import { cardApi, selectCard, selectConfig, store } from 'lxl-data';
import { LxlConnectedElement } from 'lxl-utils/lxl-connected-element';
import { lxlPortal } from 'lxl-utils/lxl-portal';
import modalStyles from './lxl-widget-card-info-modal.scss';
import skeletonStyles from './lxl-widget-card-info-skeleton.scss';
import styles from './lxl-widget-card-info.scss';
import { deriveUiFlags } from 'lxl-utils/lxl-derive-ui';

@customElement('lxl-widget-card-info')
export class LxlWidgetCardInfo extends LxlConnectedElement {
  static styles = styles;

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    mode: 'open' as ShadowRootMode,
  };

  @state() showModal: boolean;

  query = cardApi.endpoints.getCard;

  @query('#ariaLiveAnnouncer') ariaLiveAnnouncer: HTMLElement;

  selector = selectCard;

  private focusIndex = 0;

  private _googleWalletDispatched = false;

  @queryAll('[tabindex="0"]') focusableElements!: NodeListOf<HTMLElement>;

  firstUpdated() {
    this.shadowRoot!.addEventListener('keydown', this.handleKeyDown.bind(this));
    this.updateFocus();
  }

  updateFocus() {
    if (this.focusableElements.length > 0 && this.focusIndex < this.focusableElements.length) {
      this.focusableElements[this.focusIndex].focus();
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    const maxIndex = this.focusableElements.length - 1;

    switch (event.key) {
      case 'ArrowRight':
        this.focusIndex = this.focusIndex + 1 > maxIndex ? 0 : this.focusIndex + 1;
        this.updateFocus();
        event.preventDefault();
        break;

      case 'ArrowLeft':
        this.focusIndex = this.focusIndex - 1 < 0 ? maxIndex : this.focusIndex - 1;
        this.updateFocus();
        event.preventDefault();
        break;

      case 'Enter':
        const targetElement = event.target as HTMLElement;
        if (targetElement && targetElement.classList.contains('lxl-widget-card-info-barcode')) {
          this.showModal = true;
          this.ariaLiveAnnouncer.textContent = this.localize('LXL_ACCESSIBILITY_MODAL_OPENED');
          this.requestUpdate();
          event.preventDefault();
        }
        break;
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('showModal')) {
      const ariaMessage = this.showModal
        ? this.localize('LXL_ACCESSIBILITY_MODAL_OPENED')
        : this.localize('LXL_ACCESSIBILITY_MODAL_CLOSED');
      this.ariaLiveAnnouncer.textContent = ariaMessage;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._googleWalletDispatched = false;
  }

  stateChanged(state: any) {
    super.stateChanged(state);
    if (!this._googleWalletDispatched && this.jwt && this.locale) {
      const cfg = selectConfig(state)?.config;
      if (cfg?.banner) {
        const ui = deriveUiFlags(cfg);
        if (ui.showWallets) {
          this._googleWalletDispatched = true;
          store.dispatch(cardApi.endpoints.getGoogleWalletUrl.initiate({ locale: this.locale, jwt: this.jwt }));
        } else {
          this._googleWalletDispatched = true;
        }
      }
    }
  }

  lxlConnectedLoadingRender() {
    return html` <lxl-widget-card-info-skeleton></lxl-widget-card-info-skeleton> `;
  }

  lxlConnectedRender() {
    const cfg = selectConfig(store.getState())?.config;
    const ui = deriveUiFlags(cfg);
    // const digitalCard = cfg?.digitalCard;
    // const isDigitalCard = digitalCard === "true" || digitalCard === true;

    const firstName = this.data?.loyaltyProfile?.name ?? '';
    const lastName = this.data?.loyaltyProfile?.surname ?? '';
    const tierName = this.data?.currentTier?.configuration?.tierName ?? '';
    const activation = this.data?.loyaltyCard?.cardActivationDate ? new Date(this.data.loyaltyCard.cardActivationDate) : null;
    const label = this.localize?.('LXL_WIDGET_INFO_CARD_MEMBER_SINCE') as string;
    const memberSince =
      activation && ui.showMemberSinceOnCard
        ? `${label} ${new Intl.DateTimeFormat(this.locale, { month: '2-digit', year: '2-digit' }).format(activation)}`
        : '';

    return html`
      <lxl-component-card>
        <div
          id="ariaLiveAnnouncer"
          aria-live="assertive"
          style="position: absolute; width: 1px; height: 1px; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0);"
        ></div>
        <div class="lxl-widget-card-info" role="region" aria-labelledby="card-info-title">
          <div class="lxl-widget-card-info-content">
            <div class="lxl-widget-card-info-content-title">
              <h3 id="card-info-title" aria-label="${this.localize('LXL_WIDGET_INFO_CARD_TITLE')}">
                ${this.localize('LXL_WIDGET_INFO_CARD_TITLE')}
              </h3>
            </div>
            <div class="lxl-widget-card-info-content-joined">
              <p>${this.localize('LXL_WIDGET_INFO_CARD_JOINED')}</p>
              <p>
                <strong
                  >${this.data?.loyaltyCard?.cardActivationDate &&
                  new Date(this.data?.loyaltyCard?.cardActivationDate).toLocaleDateString(this.locale)}</strong
                >
              </p>
            </div>
            <div class="lxl-widget-card-info-content-card-number">
              <p>${this.localize('LXL_WIDGET_INFO_CARD_NUMBER')}</p>
              <p><strong>${this.data?.loyaltyCard?.cardNumber}</strong></p>
            </div>
            ${ui.showWallets
              ? html`
                  <div class="lxl-widget-card-info-content-description">
                    <p>${this.localize('LXL_WIDGET_INFO_CARD_DESCRIPTION')}</p>
                  </div>

                  <div class="lxl-widget-card-info-content-wallets">
                    <div class="lxl-widget-card-info-content-wallets-google">
                      <lxl-component-wallet-google
                        labelText="${this.localize('LXL_ACCESSIBILITY_GOOGLE_WALLET')}"
                        aria-label="${this.localize('LXL_ACCESSIBILITY_GOOGLE_WALLET')}"
                        class="wallet-google"
                        tabindex="0"
                        jwt="${this.jwt}"
                        locale="${this.locale}"
                        google-wallet-url=${this.data?.loyaltyCard?.googleWallet}
                        button-icon-url=${`${this.assetsGlobalUrl}/wallets/google/${
                          this.locale.split('-')[0] || 'en'
                        }_add_to_google_wallet_add-wallet-badge.svg`}
                      >
                      </lxl-component-wallet-google>
                    </div>
                    <div class="lxl-widget-card-info-content-wallets-apple">
                      <lxl-component-wallet-apple
                        aria-label="${this.localize('LXL_ACCESSIBILITY_APPLE_WALLET')}"
                        class="wallet-apple"
                        tabindex="0"
                        locale="${this.locale}"
                        jwt="${this.jwt}"
                        card-url=${this.data?.loyaltyCard?.appleWallet}
                        button-icon-url=${`${this.assetsGlobalUrl}/wallets/apple/${(
                          this.locale.split('-')[0] || 'en'
                        ).toUpperCase()}_Add_to_Apple_Wallet.svg`}
                      >
                      </lxl-component-wallet-apple>
                    </div>
                  </div>
                `
              : ''}
          </div>
          <div
            tabindex="0"
            class="lxl-widget-card-info-barcode"
            @click="${() => (this.showModal = true)}"
            aria-label="${this.localize('LXL_WIDGET_INFO_CARD_TITLE')}: ${this.data?.loyaltyCard?.cardNumber}"
            onclick="javascript:tealium_data2track.push({id: 'Click', data_element_id: 'LoyaltyWidget_Link', data_analytics_available_call:'0'});"
          >
            <lxl-component-loyalty-card
              cardimageurl=${`${this.assetsUrl}/${ui.loyaltyCardImageFilename}`}
              card-number="${this.data?.loyaltyCard?.cardNumber}"
              .showBarcode=${ui.showBarcodeOnCard}
              first-name="${this.data?.loyaltyProfile?.name ?? ''}"
              last-name="${this.data?.loyaltyProfile?.surname ?? ''}"
              tier-name="${this.data?.currentTier?.configuration?.tierName ?? ''}"
              member-since="${memberSince}"
            >
            </lxl-component-loyalty-card>
          </div>
        </div>
      </lxl-component-card>
      ${lxlPortal(
        this.showModal,
        html`
          <lxl-widget-card-info-modal
            tabindex="-1"
            title="${this.localize('LXL_WIDGET_INFO_CARD_TITLE')}"
            cardimageurl=${`${this.assetsUrl}/${ui.loyaltyCardImageFilename}`}
            card-number="${this.data?.loyaltyCard?.cardNumber}"
            .showBarcode=${ui.showBarcodeOnCard}
            first-name="${firstName}"
            last-name="${lastName}"
            tier-name="${tierName}"
            member-since="${memberSince}"
            aria-label="${this.localize('LXL_WIDGET_INFO_MODAL')}"
          ></lxl-widget-card-info-modal>
        `,
        () => (this.showModal = false),
      )}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lxl-widget-card-info': LxlWidgetCardInfo;
  }
}

@customElement('lxl-widget-card-info-modal')
export class LxlWidgetCardInfoModal extends LitElement {
  static styles = modalStyles;

  @property() title;
  @property() 'card-number';
  @property({ type: String, attribute: 'cardimageurl' }) cardImageUrl;
  @property({ type: Boolean, attribute: 'show-barcode' }) showBarcode: boolean = true;
  @property({ type: String, attribute: 'first-name' }) firstName: string = '';
  @property({ type: String, attribute: 'last-name' }) lastName: string = '';
  @property({ type: String, attribute: 'tier-name' }) tierName: string = '';
  @property({ type: String, attribute: 'member-since' }) memberSince = '';

  @query('.lxl-widget-card-info-barcode-modal') modalContent;

  render() {
    return html`
      <lxl-component-modal prevent-backdrop-close>
        <div class="lxl-widget-card-info-barcode-modal">
          <h3 tabindex="0">${this.title}</h3>
          <div class="lxl-widget-card-info-barcode-modal-card">
            <lxl-component-loyalty-card
              cardimageurl=${this.cardImageUrl}
              card-number="${this['card-number']}"
              .showBarcode=${this.showBarcode}
              first-name="${this.firstName}"
              last-name="${this.lastName}"
              tier-name="${this.tierName}"
              member-since="${this.memberSince}"
            >
            </lxl-component-loyalty-card>
          </div>
        </div>
      </lxl-component-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lxl-widget-card-info-modal': LxlWidgetCardInfoModal;
  }
}

@customElement('lxl-widget-card-info-skeleton')
export class LxlWidgetCardInfoSkeleton extends LitElement {
  static styles = [styles, skeletonStyles];

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    mode: 'open' as ShadowRootMode,
  };

  render() {
    return html`
      <lxl-component-card>
        <div class="lxl-widget-card-info">
          <div class="lxl-widget-card-info-content">
            <div class="lxl-widget-card-info-content-points">
              <lxl-component-skeleton height="28"></lxl-component-skeleton>
              <lxl-component-skeleton height="14"></lxl-component-skeleton>
              <lxl-component-skeleton height="14"></lxl-component-skeleton>
            </div>
            <div class="lxl-widget-card-info-content-wallets">
              <div class="lxl-widget-card-info-content-wallets-google">
                <lxl-component-skeleton button height="40"></lxl-component-skeleton>
              </div>
              <div class="lxl-widget-card-info-content-wallets-apple">
                <lxl-component-skeleton button height="40"></lxl-component-skeleton>
              </div>
            </div>
          </div>
          <div class="lxl-widget-card-info-barcode">
            <lxl-component-skeleton></lxl-component-skeleton>
          </div>
        </div>
      </lxl-component-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'lxl-widget-card-info-skeleton': LxlWidgetCardInfoSkeleton;
  }
}
